import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../lib/api';
import { useFocusTrap } from './useFocusTrap';

const WARN_SECONDS = 60;
const REFRESH_EVERY_MS = 10 * 60 * 1000;

export function useIdleLogout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [warningSeconds, setWarningSeconds] = useState<number | null>(null);
  const lastActivity = useRef(Date.now());
  const lastRefresh = useRef(Date.now());
  const warningRef = useRef<number | null>(null);
  const timeoutMinutes = user?.idle_timeout_minutes ?? 30;

  const signOutIdle = useCallback(async () => {
    await logout('idle');
    navigate('/login?signed_out=idle');
  }, [logout, navigate]);

  const staySignedIn = useCallback(() => {
    lastActivity.current = Date.now();
    warningRef.current = null;
    setWarningSeconds(null);
    void api.post('/api/auth/refresh', {}).catch(() => undefined);
    lastRefresh.current = Date.now();
  }, []);

  useEffect(() => {
    if (!user) {
      warningRef.current = null;
      setWarningSeconds(null);
      return;
    }

    const onActivity = () => {
      // While the warning dialog is open, only explicit Stay/Sign-out actions dismiss it.
      if (warningRef.current != null) return;
      lastActivity.current = Date.now();
      if (Date.now() - lastRefresh.current >= REFRESH_EVERY_MS) {
        lastRefresh.current = Date.now();
        void api.post('/api/auth/refresh', {}).catch(() => undefined);
      }
    };

    const events: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];
    for (const event of events) {
      window.addEventListener(event, onActivity, { passive: true });
    }

    const tick = window.setInterval(() => {
      const idleMs = Date.now() - lastActivity.current;
      const limitMs = timeoutMinutes * 60 * 1000;
      const remainingMs = limitMs - idleMs;
      if (remainingMs <= 0) {
        void signOutIdle();
        return;
      }
      if (remainingMs <= WARN_SECONDS * 1000) {
        const secs = Math.max(1, Math.ceil(remainingMs / 1000));
        warningRef.current = secs;
        setWarningSeconds(secs);
      } else if (warningRef.current != null) {
        warningRef.current = null;
        setWarningSeconds(null);
      }
    }, 1000);

    return () => {
      for (const event of events) {
        window.removeEventListener(event, onActivity);
      }
      window.clearInterval(tick);
    };
  }, [user, timeoutMinutes, signOutIdle]);

  return { warningSeconds, staySignedIn, signOutIdle };
}

export function IdleLogoutModal({
  warningSeconds,
  staySignedIn,
  signOutIdle,
}: {
  warningSeconds: number | null;
  staySignedIn: () => void;
  signOutIdle: () => void;
}) {
  const open = warningSeconds != null;
  const trapRef = useFocusTrap(open, staySignedIn, { initialFocus: 'last' });

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ink-950/50 p-4">
      <div
        ref={trapRef}
        className="w-full max-w-md rounded-xl bg-panel p-5 shadow-xl"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="idle-logout-title"
        aria-describedby="idle-logout-desc"
        tabIndex={-1}
      >
        <h2 id="idle-logout-title" className="font-display text-lg font-semibold text-ink-950">
          Still there?
        </h2>
        <p id="idle-logout-desc" className="mt-2 text-sm text-ink-700">
          You will be signed out in <strong>{warningSeconds}</strong> second
          {warningSeconds === 1 ? '' : 's'} due to inactivity.
        </p>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={() => void signOutIdle()}>
            Sign out now
          </button>
          <button type="button" className="btn-primary" onClick={staySignedIn}>
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
